// src/data/importPipelineClient.js
//
// INGEST-FRAMEWORK-S1: Browser-side client for the staging pipeline API.
// All functions return promises and throw on non-2xx responses.

const BASE = "/api/import";

async function _json(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.code       = body.error;
    err.reason     = body.reason;
    err.parseErrors = body.parseErrors;
    err.httpStatus = res.status;
    throw err;
  }
  return body;
}

/**
 * Upload one or more files to the staging pipeline.
 *
 * For PDF files, multiple documents can be provided and are synthesised by
 * the LLM in a single call (PDF-ABSTRACTION-MODEL-OVERHAUL).
 * For CSV/TXT, only the first file is used.
 *
 * @param {File | File[]} fileOrFiles
 * @param {object} [opts]
 * @param {string} [opts.overrideDataType]  INGEST-PIPELINE-TYPE-AWARE-S1: force
 *   a specific registered data type (e.g. "badging") instead of relying on
 *   auto-detection — used when the Step 1 UI's override dropdown is used.
 * @returns {Promise<{
 *   sessionId: number,
 *   rowCount: number,
 *   columnCount: number,
 *   parseErrors: object[],
 *   detectedSourceType: string,
 *   suggestedProfileId: number|null,
 *   columnMappings: Array<{csvColumn:string, canonicalField:string|null, confidence:"high"|"medium"|"low"}>,
 *   extractedFields?: object[],
 *   extractedFieldCount?: number,
 *   detectedDataType?: string,
 *   dataTypeReason?: string,
 *   ambiguousDataType?: boolean,
 *   availableDataTypes?: Array<{key:string,label:string,description:string}>,
 * }>}
 */
export async function stageImportFile(fileOrFiles, opts = {}) {
  const form = new FormData();
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  const allPdfs = files.every((f) => /\.pdf$/i.test(f.name));

  if (allPdfs && files.length > 1) {
    // Multi-file PDF path: append each file under the "files" field name so
    // the server can identify this as a multi-document package.
    for (const f of files) {
      form.append("files", f);
    }
  } else {
    // Single file (CSV, TXT, or single PDF).
    form.append("file", files[0]);
  }
  if (opts.overrideDataType) form.append("overrideDataType", opts.overrideDataType);

  const res = await fetch(`${BASE}/stage`, { method: "POST", body: form });
  return _json(res);
}

/**
 * List all saved source profiles.
 * @returns {Promise<Array<{id:number, name:string, source_type:string, column_map:object}>>}
 */
export async function getSourceProfiles() {
  const res = await fetch(`${BASE}/source-profiles`);
  return _json(res);
}

/**
 * Create or update a source profile.
 * @param {{
 *   name:         string,
 *   source_type:  string,
 *   column_map:   object,
 *   dispositions?: object   per-column disposition overrides
 * }} data
 * @returns {Promise<{id:number, name:string, source_type:string, column_map:object, dispositions:object}>}
 */
export async function saveSourceProfile(data) {
  const res = await fetch(`${BASE}/source-profiles`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return _json(res);
}

/**
 * Get normalized staged rows for a wizard session.
 * @param {number} sessionId
 * @returns {Promise<{sessionId:number, rowCount:number, rows:object[]}>}
 */
export async function getStagedRows(sessionId) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/staged-rows`);
  return _json(res);
}

/**
 * Run the validation suite on a staged session.
 * @param {number} sessionId
 * @returns {Promise<{
 *   status: "passed"|"warnings"|"errors",
 *   rowCount: number,
 *   items: Array<{severity:"error"|"warning", field:string, message:string, rowIndex:number}>
 * }>}
 */
export async function validateStagedSession(sessionId) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/validate`, { method: "POST" });
  return _json(res);
}

/**
 * Update the normalized_data of a single staged row.
 * Server invalidates the diff cache automatically.
 *
 * @param {number} sessionId
 * @param {number} rowId
 * @param {object} normalizedData — full replacement for normalized_data
 * @returns {Promise<{id:number, row_index:number, normalized_data:object, validation_status:string}>}
 */
export async function patchStagedRow(sessionId, rowId, normalizedData) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/staged-rows/${rowId}`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ normalized_data: normalizedData }),
  });
  return _json(res);
}

/**
 * Delete a single staged row. Server invalidates the diff cache automatically.
 * Returns undefined on success (204 No Content).
 *
 * @param {number} sessionId
 * @param {number} rowId
 * @returns {Promise<void>}
 */
export async function deleteStagedRow(sessionId, rowId) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/staged-rows/${rowId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
}

/**
 * Stamp a Ripple location ID on every staged row of a PDF import session.
 * Must be called before Validate so the location identifier resolves the
 * "Row has no identifier" validation error.
 *
 * @param {number} sessionId
 * @param {string} locationId  e.g. "EMEA-DUB-01"
 * @returns {Promise<{ ok: boolean, sessionId: number, locationId: string }>}
 */
export async function assignSessionLocation(sessionId, locationId) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/assign-location`, {
    method:      "POST",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
    body:        JSON.stringify({ locationId }),
  });
  return _json(res);
}

/**
 * Check whether the LLM proxy is reachable.
 * Useful for admins to diagnose "no extracted fields" vs "service down".
 *
 * @returns {Promise<{ ok: boolean, provider: string, error?: string }>}
 */
export async function getPdfHealth() {
  const res = await fetch(`${BASE}/pdf-health`);
  return _json(res);
}

/**
 * Compute the smart diff for a staged session (read-only against live data).
 *
 * @param {number} sessionId
 * @param {object} [opts]
 * @param {string} [opts.classification]  Optional filter: "material"|"review"|"new"|"unchanged"
 * @param {number} [opts.page=1]          1-indexed page number
 * @param {number} [opts.limit=50]        Records per page (server caps at 200)
 * @returns {Promise<{
 *   summary: {material:number, review:number, new:number, unchanged:number, normalizedAway:number},
 *   records: Array<object>,
 *   pagination: {page:number, limit:number, total:number, totalPages:number, classification:string|null},
 * }>}
 */
export async function computeSessionDiff(sessionId, { classification, page = 1, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (classification) params.set("classification", classification);
  params.set("page",  String(page));
  params.set("limit", String(limit));
  const res = await fetch(`${BASE}/sessions/${sessionId}/diff?${params}`, { method: "POST" });
  return _json(res);
}

/**
 * INGEST-PIPELINE-TYPE-AWARE-S1: lock a staged session to a confirmed data
 * type ("leases" | "badging") before proceeding past Step 1.
 *
 * @param {number} sessionId
 * @param {string} dataType
 * @returns {Promise<{id:number, confirmed_data_type:string, type_locked_at:string}>}
 */
export async function confirmDataType(sessionId, dataType) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/confirm-type`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ dataType }),
  });
  return _json(res);
}

/**
 * INGEST-PIPELINE-TYPE-AWARE-S1: acknowledge/dismiss a peak>3x anomaly flag
 * on a badging staged row. Values are never modified — only the
 * acknowledgement timestamp is written, so the warning does not re-surface.
 *
 * @param {number} sessionId
 * @param {number} rowIndex
 * @returns {Promise<{session_id:number, row_index:number, anomaly_acknowledged_at:string}>}
 */
export async function acknowledgeAnomaly(sessionId, rowIndex) {
  const res = await fetch(
    `${BASE}/sessions/${sessionId}/staged-rows/${rowIndex}/acknowledge-anomaly`,
    { method: "POST" },
  );
  return _json(res);
}

/**
 * INGEST-PIPELINE-TYPE-AWARE-S1: Step 4 review summary for a confirmed
 * badging/utilization session — grouped by location with new/updated counts.
 *
 * @param {number} sessionId
 * @returns {Promise<{sessionId:number, dataType:string, reviewCopy:object, locations:object[], totals:object}>}
 */
export async function getBadgingReview(sessionId) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/badging-review`);
  return _json(res);
}

/**
 * INGEST-HEADCOUNT-TYPE-S1: Step 4 review summary for a confirmed headcount
 * session — per-office ft/vc/ic breakdown with new/updated status, Work
 * Location resolution counts, and aggregate remote/distributed totals.
 *
 * @param {number} sessionId
 * @returns {Promise<{sessionId:number, dataType:string, asOfDate:string, reviewCopy:object, locations:object[], inOfficeRowCount:number, remoteDistributedRowCount:number, totalRowCount:number, resolvedNameCount:number, unmappedNames:string[], totals:object}>}
 */
export async function getHeadcountReview(sessionId) {
  const res = await fetch(`${BASE}/sessions/${sessionId}/headcount-review`);
  return _json(res);
}

/**
 * Update an existing source profile.
 * @param {number} profileId
 * @param {{name?:string, column_map?:object, dispositions?:object}} data
 * @returns {Promise<{id:number, name:string, source_type:string, column_map:object, dispositions:object}>}
 */
export async function updateSourceProfile(profileId, data) {
  const res = await fetch(`${BASE}/source-profiles/${profileId}`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(data),
  });
  return _json(res);
}

/**
 * Delete a saved source profile.
 * @param {number} profileId
 * @returns {Promise<{ok:boolean, id:number}>}
 */
export async function deleteSourceProfile(profileId) {
  const res = await fetch(`${BASE}/source-profiles/${profileId}`, { method: "DELETE" });
  return _json(res);
}
